import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// Create the Navigation Context
const NavigationContext = createContext(null);

/**
 * Provider component for navigation state management
 * Tracks and persists language and course selections across the application
 */
export const NavigationProvider = ({ children }) => {
  // Primary state for tracking selected language and course
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [currentCourse, setCurrentCourse] = useState(null);
  
  // Additional navigation state that might be useful
  const [navigationHistory, setNavigationHistory] = useState([]);
  const location = useLocation();

  // Track route changes for history and special route handling
  useEffect(() => {
    // Add current path to navigation history - memoized to prevent unnecessary updates
    setNavigationHistory(prev => {
      const newPath = location.pathname;
      const lastPath = prev[prev.length - 1];
      
      // Only update if the path actually changed
      if (lastPath !== newPath) {
        return [...prev.slice(-4), newPath];
      }
      return prev;
    });
    
    // Special route handling
    if (location.pathname === '/languages') {
      // When user explicitly navigates to language selection,
      // we don't clear the language but we should clear the course
      // as they're likely changing languages
      setCurrentCourse(prevCourse => {
        if (prevCourse !== null) {
          localStorage.removeItem('currentCourse');
          return null;
        }
        return prevCourse;
      });
    }
  }, [location.pathname]);

  // Initialize state from localStorage on mount
  useEffect(() => {
    try {
      // Restore language selection
      const savedLanguage = localStorage.getItem('selectedLanguage');
      if (savedLanguage) {
        console.log('Saved language ', savedLanguage);
        setSelectedLanguage(JSON.parse(savedLanguage));
      }
      
      // Restore course selection
      const savedCourse = localStorage.getItem('currentCourse');
      if (savedCourse) {
        setCurrentCourse(JSON.parse(savedCourse));
      }
    } catch (error) {
      console.error('Error restoring navigation state:', error);
      // Clear potentially corrupted storage
      localStorage.removeItem('selectedLanguage');
      localStorage.removeItem('currentCourse');
    }
  }, []);

  /**
   * Update the selected language and persist to localStorage
   * Using useCallback to maintain stable function reference
   */
  const updateLanguage = useCallback((language) => {
    if (!language) return;
    
    // Update state
    console.log('lang ', language);
    setSelectedLanguage(prevLanguage => {
      // Only update if language actually changed
      if (JSON.stringify(prevLanguage) !== JSON.stringify(language)) {
        // Persist to localStorage
        try {
          localStorage.setItem('selectedLanguage', JSON.stringify(language));
        } catch (error) {
          console.error('Error saving language to localStorage:', error);
        }
        return language;
      }
      return prevLanguage;
    });
  }, []);

  /**
   * Update the current course and persist to localStorage
   * Using useCallback to maintain stable function reference
   */
  const updateCourse = useCallback((course) => {
    if (!course) return;
    
    // Update state with prevState callback to avoid unnecessary updates
    setCurrentCourse(prevCourse => {
      // Skip update if nothing changed
      if (prevCourse && course && prevCourse._id === course._id && 
          JSON.stringify(prevCourse) === JSON.stringify(course)) {
        return prevCourse;
      }
      
      // Persist to localStorage if changing
      try {
        localStorage.setItem('currentCourse', JSON.stringify(course));
      } catch (error) {
        console.error('Error saving course to localStorage:', error);
      }
      
      return course;
    });
  }, []);

  /**
   * Clear all navigation context data
   * Using useCallback to maintain stable function reference
   */
  const clearNavigationContext = useCallback(() => {
    setSelectedLanguage(null);
    setCurrentCourse(null);
    localStorage.removeItem('selectedLanguage');
    localStorage.removeItem('currentCourse');
  }, []);

  // FIXED: Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    // State
    selectedLanguage,
    currentCourse,
    navigationHistory,
    
    // Methods
    updateLanguage,
    updateCourse,
    clearNavigationContext
  }), [
    selectedLanguage,
    currentCourse,
    navigationHistory,
    updateLanguage,
    updateCourse,
    clearNavigationContext
  ]);

  return (
    <NavigationContext.Provider value={contextValue}>
      {children}
    </NavigationContext.Provider>
  );
};

/**
 * Custom hook to use the navigation context
 */
export const useNavigation = () => {
  const context = useContext(NavigationContext);
  
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  
  return context;
};